import { DateTime } from "luxon";
import type { Generator } from "../../../../../utils/backend-types";
import type { ExcelCompatibleTableData } from "../../../../../utils/files/downloadAsExcel";
import type { AusfallarbeitTimeSeriesResponse } from "../../../../RevenueMonitoring/RevenueMonitoring.types";
import { getAbreglerLabel, SLOT_DURATION_MS } from "../Ausfallarbeit.constants";

const EXPORT_TIMEZONE = "Europe/Berlin";

type GeneratorExportColumn = {
  label: string;
  valuesByTimestamp: Map<string, number | null>;
};

function buildColumnLabel(generator: Generator | undefined, abregler: string) {
  const generatorLabel = generator?.name ?? "Unbekannte Anlage";
  const trIdLabel = generator?.trId ?? "ohne TR-ID";
  return `${generatorLabel} (${trIdLabel}, ${abregler})`;
}

function getOrCreateColumn(
  columnsByKey: Map<string, GeneratorExportColumn>,
  generatorById: Map<number, Generator>,
  series: AusfallarbeitTimeSeriesResponse[number]
): GeneratorExportColumn {
  const key = `${series.generator_id}:${series.abregler ?? "unknown"}`;
  const existingColumn = columnsByKey.get(key);
  if (existingColumn) return existingColumn;

  const generator = generatorById.get(series.generator_id);
  const column: GeneratorExportColumn = {
    label: buildColumnLabel(generator, getAbreglerLabel(series.abregler)),
    valuesByTimestamp: new Map()
  };
  columnsByKey.set(key, column);
  return column;
}

function buildColumns(
  ausfallarbeitData: AusfallarbeitTimeSeriesResponse,
  generators: Array<Generator>
): GeneratorExportColumn[] {
  const generatorById = new Map(
    generators.map((generator) => [generator.id, generator])
  );

  const columnsByKey = new Map<string, GeneratorExportColumn>();

  ausfallarbeitData.forEach((series) => {
    const column = getOrCreateColumn(columnsByKey, generatorById, series);

    series.index.forEach((timestamp, i) => {
      column.valuesByTimestamp.set(timestamp, series.values[i]);
    });
  });

  return [...columnsByKey.values()].filter((column) =>
    [...column.valuesByTimestamp.values()].some((value) => value !== null)
  );
}

function getSortedTimestamps(
  ausfallarbeitData: AusfallarbeitTimeSeriesResponse
): string[] {
  return [...new Set(ausfallarbeitData.flatMap((series) => series.index))].sort(
    (a, b) =>
      DateTime.fromISO(a, { setZone: true }).toMillis() -
      DateTime.fromISO(b, { setZone: true }).toMillis()
  );
}

function buildRow(
  timestamp: string,
  columns: GeneratorExportColumn[]
): Array<string | number> {
  const start = DateTime.fromISO(timestamp, { setZone: true }).setZone(
    EXPORT_TIMEZONE
  );
  const end = start.plus({ milliseconds: SLOT_DURATION_MS });

  const values = columns.map(
    (column) => column.valuesByTimestamp.get(timestamp) ?? ""
  );

  return [start.toISO() ?? "", end.toISO() ?? "", ...values];
}

/**
 * Builds an Excel-compatible table in a time-series wide format:
 * one row per timeslot, one column per generator/TR-ID.
 */
export function buildAusfallarbeitExportTableData(
  ausfallarbeitData: AusfallarbeitTimeSeriesResponse,
  generators: Array<Generator>
): ExcelCompatibleTableData {
  const columns = buildColumns(ausfallarbeitData, generators);
  const sortedTimestamps = getSortedTimestamps(ausfallarbeitData);

  const header = [
    "Zeit von",
    "Zeit bis",
    ...columns.map((column) => column.label)
  ];
  const dataRows = sortedTimestamps.map((timestamp) =>
    buildRow(timestamp, columns)
  );

  return [header, ...dataRows];
}
